import type {
  IGroupInfo,
  IProductGroupSeoContentBlock,
  IProductGroupTrustModule,
  IProductGroupTrustModuleType,
} from '@/api/types/product';
import { normalizeGoodsListFaq } from './goodsListFaq.utils';

export interface GoodsListSeoFooterViewModel {
  faqItems: NonNullable<IGroupInfo['faq_list']>;
  faqTitle: string;
  hOne: string;
  seoContentBlocks: IProductGroupSeoContentBlock[];
  trustModules: IProductGroupTrustModule[];
}

export type GoodsListSeoFaqRendering = 'external' | 'legacy';

const SEO_FOOTER_GROUP_FIELDS = [
  'h_one',
  'faq_title',
  'faq_list',
  'seo_content_blocks',
  'trust_modules',
] as const satisfies ReadonlyArray<keyof IGroupInfo>;

const TRUST_MODULE_TYPES = new Set<IProductGroupTrustModuleType>([
  'free_shipping',
  'warranty',
  'why_choose',
  'text',
]);

const normalizeText = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

export function createGoodsListSeoFooterViewModel(group: IGroupInfo): GoodsListSeoFooterViewModel {
  const seoContentBlocks = (Array.isArray(group.seo_content_blocks) ? group.seo_content_blocks : [])
    .filter((item) => normalizeText(item?.title) && normalizeText(item?.content))
    .toSorted((left, right) => left.sort - right.sort)
    .slice(0, 3)
    .map((item) => ({
      ...item,
      title: normalizeText(item.title),
      content: normalizeText(item.content),
    }));

  const trustModules = (Array.isArray(group.trust_modules) ? group.trust_modules : [])
    .filter(
      (item) =>
        TRUST_MODULE_TYPES.has(item?.type) &&
        normalizeText(item?.title) &&
        normalizeText(item?.description),
    )
    .toSorted((left, right) => left.sort - right.sort)
    .slice(0, 3)
    .map((item) => ({
      ...item,
      icon: normalizeText(item.icon),
      title: normalizeText(item.title),
      description: normalizeText(item.description),
    }));

  const faqItems = normalizeGoodsListFaq(group.faq_list);

  return {
    faqItems,
    faqTitle: normalizeText(group.faq_title),
    hOne: normalizeText(group.h_one),
    seoContentBlocks,
    trustModules,
  };
}

export function createGoodsListClientGroup(group: IGroupInfo): IGroupInfo {
  const clientGroup = { ...group };

  SEO_FOOTER_GROUP_FIELDS.forEach((field) => {
    delete clientGroup[field];
  });

  return clientGroup;
}

export function createLegacyGoodsListSeoGroup(
  group: IGroupInfo,
  rendering: GoodsListSeoFaqRendering,
): IGroupInfo {
  return rendering === 'external' ? createGoodsListClientGroup(group) : group;
}

export function shouldRefreshExternalGoodsListSeoFooter(
  rendering: GoodsListSeoFaqRendering,
): boolean {
  return rendering === 'external';
}

export async function runWithGoodsListSeoFooterRefresh<T>(
  rendering: GoodsListSeoFaqRendering,
  refresh: () => void,
  task: () => Promise<T>,
): Promise<T> {
  try {
    return await task();
  } finally {
    if (shouldRefreshExternalGoodsListSeoFooter(rendering)) {
      refresh();
    }
  }
}
